/** @format */
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	ElementRef,
	inject,
	input,
	Signal,
	viewChild
} from '@angular/core';
import { CompareProductDetails } from '@compare/data-access';
import {
	selectCompareProductDetails,
	selectFeatureFlags,
	selectProductListSEO
} from '@plp/data-access/state';

import { AnalyticsService } from '@shared/data-access/services';
import { CommonModule } from '@angular/common';
import { CompareThumbnailComponent } from './compare-thumbnail/compare-thumbnail.component';
import { HmfButtonComponent } from '@dsg-tech/homefield-angular';
import { ProductListCompareService } from '@plp/data-access/services';
import { CompareBarProduct, ProductListSEO } from '@shared/data-access/interfaces';
import { Store } from '@ngrx/store';

@Component({
	selector: 'plp-components-compare-bar',
	imports: [CommonModule, CompareThumbnailComponent, HmfButtonComponent],
	templateUrl: './compare-bar.component.html',
	styleUrl: './compare-bar.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompareBarComponent {
	private readonly compareService = inject(ProductListCompareService);
	private readonly analyticsService = inject(AnalyticsService);
	private readonly store = inject(Store);

	readonly compareBarContainer = viewChild<ElementRef<HTMLDivElement>>('compareBarContainer');

	readonly productListSeoData: Signal<ProductListSEO | null> =
		this.store.selectSignal(selectProductListSEO);
	readonly compareProductDetails: Signal<CompareProductDetails> = this.store.selectSignal(
		selectCompareProductDetails
	);
	readonly featureFlags: Signal<Record<string, boolean>> =
		this.store.selectSignal(selectFeatureFlags);

	readonly compareList: Signal<CompareBarProduct[]> = input([]);
	readonly showThumbnails = input(false);
	readonly compareGridEnabled: Signal<boolean> = computed(
		() => this.featureFlags().compareGridEnabled
	);

	removeFromCompareList(ecode: string, index: number): void {
		if (this.compareList().length === 1) {
			this.moveFocusAfterRemoveAllItems();
		} else {
			const buttonsNodeList: NodeListOf<HTMLButtonElement> =
				this.compareBarContainer()?.nativeElement?.querySelectorAll(
					`.compare-actions-container button`
				);

			if (buttonsNodeList.length > 0) {
				const buttonsArray: HTMLButtonElement[] = Array.from(buttonsNodeList);

				if (index + 1 < buttonsArray.length) {
					buttonsArray[index + 1].focus();
				}
			}
		}

		this.compareService.removeProductFromCompareList(ecode);
	}

	clearAllCompareList(): void {
		this.moveFocusAfterRemoveAllItems();
		this.compareService.clearCompareList();
	}

	moveFocusAfterRemoveAllItems(): void {
		if (this.showThumbnails()) {
			// desktop - to the first accordion header
			const firstAccordionHeader: HTMLElement = document.querySelector(
				'.plp-srlp-filters .accordion-header'
			);
			if (firstAccordionHeader) {
				firstAccordionHeader.focus();
			}
		} else {
			// mobile - to the first product card title
			const firstProductCardTitle: HTMLElement = document.querySelector(
				'.plp-card-container .product-title-link'
			);
			if (firstProductCardTitle) {
				firstProductCardTitle.focus();
			}
		}
	}

	compareProducts(): void {
		if (this.compareGridEnabled()) {
			this.compareService.openCompareGrid();
			this.verifyAndCallProductDetailsFromApi();
		} else {
			this.compareService.openPaintedDoorModal();
		}

		this.compareProductsAnalytics();
	}

	private compareProductsAnalytics(): void {
		const compareAnalyticsData = {
			ModalName: 'Compare Products',
			ProductArray: []
		};
		this.compareList().forEach((product) => {
			compareAnalyticsData.ProductArray.push({
				ProductID: product.ecode,
				CompareSource: product.compareSource
			});
		});
		this.analyticsService.triggerAnalyticsEvent('CompareOpened', compareAnalyticsData);
	}

	private verifyAndCallProductDetailsFromApi(): void {
		const ecodes = this.compareList().map((product) => product.ecode);
		const storeEcodes = this.compareProductDetails()?.products?.map(
			(product) => product.style.partNumber
		);

		const newEcodes = ecodes.filter((ecode: string) => !storeEcodes?.includes(ecode));

		if (newEcodes.length === 0) {
			return;
		}

		const isClearance =
			this.productListSeoData()?.identifier?.toLowerCase().startsWith('clearance') ?? false;
		this.compareService.getProductDetailsFromApi(ecodes, isClearance);
	}
}
